import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import FullscreenIcon from '@mui/icons-material/Fullscreen'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import { Box, IconButton, Slider, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { formatTime } from '../../helpers/formatTime'

/**
 * The player chrome is deliberately outside the theme palette. Ad creatives are
 * letterboxed video, and the surround has to stay black in every theme so the
 * frame edge reads as the end of the video rather than as UI bleeding into it.
 */
const VIDEO_SURROUND = '#000'
const VIDEO_CONTROL_BAR = '#111'
const VIDEO_TRACK = '#333'

export interface CreativeAdVideoPlayerHandle {
  /**
   * Seek the video to a given data-second index (0 = first data point).
   * Data-second N maps directly to video time N seconds, so the data point
   * describing the first second of video (second: 0) plays from 0s to 1s.
   * The 1-indexed labels ("1s", "2s", …) are purely a display concern.
   */
  seekTo: (dataSecond: number) => void
  /** Reset playback state and pause (used when closing the host dialog). */
  reset: () => void
}

interface CreativeAdVideoPlayerProps {
  /** Absent when the creative has no playable video; renders the placeholder. */
  videoUrl?: string
  posterUrl?: string
  videoDuration: number
  /**
   * Fired with the current data-second index (0 = first data point) the
   * playhead is inside. Null when playback hasn't started yet.
   */
  onCurrentSecondChange: (dataSecond: number | null) => void
}

/**
 * Self-contained `<video>` + transport controls. Exposes `seekTo`/`reset` via
 * `forwardRef` so parent components (timeline, close button) can drive it
 * imperatively without leaking video state upwards.
 */
export const CreativeAdVideoPlayer = forwardRef<CreativeAdVideoPlayerHandle, CreativeAdVideoPlayerProps>(
  ({ videoUrl, posterUrl, videoDuration, onCurrentSecondChange }, ref) => {
    const { t } = useTranslation('ua-insights')
    const videoRef = useRef<HTMLVideoElement>(null)

    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(true)
    const [progress, setProgress] = useState(0)
    const [videoError, setVideoError] = useState(false)
    const [currentSecond, setCurrentSecond] = useState<number | null>(null)

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (dataSecond: number) => {
          const video = videoRef.current
          if (!video) return
          video.currentTime = dataSecond
          setCurrentSecond(dataSecond)
          onCurrentSecondChange(dataSecond)
        },
        reset: () => {
          const video = videoRef.current
          if (video) {
            video.pause()
            video.currentTime = 0
          }
          setIsPlaying(false)
          setProgress(0)
          setVideoError(false)
          setCurrentSecond(null)
          onCurrentSecondChange(null)
        },
      }),
      [onCurrentSecondChange]
    )

    const handleTimeUpdate = useCallback(() => {
      const video = videoRef.current
      if (!video) return
      const videoTime = Math.floor(video.currentTime)
      setCurrentSecond(videoTime)
      onCurrentSecondChange(videoTime)
      setProgress(video.duration ? (video.currentTime / video.duration) * 100 : 0)
    }, [onCurrentSecondChange])

    const handleSliderChange = useCallback((_: Event, value: number | number[]) => {
      const video = videoRef.current
      if (!video || !video.duration) return
      video.currentTime = ((value as number) / 100) * video.duration
    }, [])

    const togglePlayPause = useCallback(() => {
      const video = videoRef.current
      if (!video) return
      if (video.paused) {
        video.play()
      } else {
        video.pause()
      }
    }, [])

    const toggleMute = useCallback(() => {
      const video = videoRef.current
      if (!video) return
      video.muted = !video.muted
      setIsMuted(video.muted)
    }, [])

    const requestFullscreen = useCallback(() => {
      const video = videoRef.current
      if (!video) return
      if (video.requestFullscreen) {
        video.requestFullscreen()
      } else if ((video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen) {
        ;(video as HTMLVideoElement & { webkitEnterFullscreen: () => void }).webkitEnterFullscreen()
      }
    }, [])

    return (
      <Box
        sx={{
          borderRadius: borderRadiusDefault,
          overflow: 'hidden',
          isolation: 'isolate',
          backgroundColor: VIDEO_SURROUND,
          // `alignSelf: stretch` lets the player grow with the surrounding
          // grid row when the right column has more content, while the vh-
          // based `maxHeight` guarantees the player (and its transport
          // controls) always stays inside the viewport in the fullScreen
          // dialog. 200px is the combined budget for the dialog title bar
          // (~64px), the info strip + divider above the card (~96px) and
          // the card's own vertical padding + a little breathing room.
          alignSelf: 'stretch',
          minHeight: 400,
          maxHeight: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, display: 'flex', minHeight: 0 }}>
          {videoError || !videoUrl ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
                backgroundColor: VIDEO_TRACK,
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 64, opacity: 0.4 }} />
              <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
                {t('video_placeholder')} ({videoDuration}s)
              </Typography>
            </Box>
          ) : (
            <Box
              component="video"
              ref={videoRef}
              src={videoUrl}
              poster={posterUrl}
              muted={isMuted}
              playsInline
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onError={() => setVideoError(true)}
              sx={{
                width: '100%',
                flex: 1,
                display: 'block',
                objectFit: 'contain',
                backgroundColor: VIDEO_SURROUND,
              }}
            />
          )}
        </Box>

        {!videoError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, backgroundColor: VIDEO_CONTROL_BAR, flexShrink: 0 }}>
            <IconButton size="small" onClick={togglePlayPause} sx={{ color: 'common.white' }}>
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <Slider
              size="small"
              value={progress}
              onChange={handleSliderChange}
              sx={{
                flex: 1,
                color: 'primary.main',
                '& .MuiSlider-thumb': { width: 12, height: 12 },
              }}
            />
            <IconButton size="small" onClick={toggleMute} sx={{ color: 'common.white' }}>
              {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </IconButton>
            <Typography variant="caption" sx={{ color: 'grey.400', width: '11ch', textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {formatTime(currentSecond ?? 0)} / {formatTime(videoDuration)}
            </Typography>
            <IconButton size="small" onClick={requestFullscreen} sx={{ color: 'common.white' }}>
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    )
  }
)

CreativeAdVideoPlayer.displayName = 'CreativeAdVideoPlayer'
